import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup, FormBuilder } from "@angular/forms";
import { FilterCompleteService } from "src/app/filter-complete/filter-complete";
import { AliquotTax } from "src/app/model/aliquot-tax";
import { Entity, Plan } from "src/app/model/entity";
import { IntegratedAdmissionAppointmentPlan } from "src/app/model/integrated-admission/integrated-admission-appointment-plan";
import { AliquotTaxService } from "src/app/services/aliquot-tax/aliquot-tax.service";
import { EntityService } from "src/app/services/entity.service";
import { IntegratedAdmissionWizardService } from "../../integrated-admission/service/integrated-admission-wizard.service";
import { BudgetWizardService } from "../services/budget.wizard.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-budget-plan",
  templateUrl: "./budget-plan.component.html",
  styleUrls: ["./budget-plan.component.css"],
})
export class BudgetPlanComponent implements OnInit, OnDestroy {
  form: FormGroup;
  entities: Entity[] = [];
  plans: Plan[] = [];
  aliquotTaxs: AliquotTax[] = [];
  filterCompleteEntity: FilterCompleteService<Entity>;
  filterCompletePlan: FilterCompleteService<Plan>;
  filterCompleteAliquotTax: FilterCompleteService<AliquotTax>;

  private currentBudgetSuscripcion: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private entityService: EntityService,
    private alicuotTaxService: AliquotTaxService,
    private service: BudgetWizardService
  ) {
    this.form = this.formBuilder.group({
      Entity: [null],
      Plan: [null],
      AliquotTax: [null],
      Name: [null],
    });

    this.initFilters();
    this.subscribeEvents();
  }

  get filterCompleteAliquotTaxOptions() {
    return this.filterCompleteAliquotTax
      ? this.filterCompleteAliquotTax.filteredOptions
      : [];
  }

  get filterCompleteEntityOptions() {
    return this.filterCompleteEntity
      ? this.filterCompleteEntity.filteredOptions
      : [];
  }

  get filterCompletePlanOptions() {
    return this.filterCompletePlan
      ? this.filterCompletePlan.filteredOptions
      : [];
  }

  async ngOnInit() {
    this.form.patchValue(
      {
        Entity: null,
        Plan: null,
        AliquotTax: null,
        Name: null,
      },
      { emitEvent: false }
    );

    await this.loadHealthInsurance();
  }

  private subscribeEvents() {
    this.currentBudgetSuscripcion = this.service.currentBudget$.subscribe(
      async (budget) => {
        this.form.patchValue(
          {
            Entity: null,
            Plan: null,
            AliquotTax: null,
            Name: budget.Name,
          },
          { emitEvent: false }
        );

        await this.loadHealthInsurance(
          budget.Plan.Entity ? budget.Plan.Entity.Id : null
        );
        if (budget.Plan.Plan && budget.Plan.Entity) {
          await this.loadInsurancePlan(budget.Plan.Entity, budget.Plan.Plan.Id);

          await this.loadAliquotTax(
            budget.Plan.Plan,
            budget.Plan.AliquotTax ? budget.Plan.AliquotTax.Id : null
          );
        }
      }
    );

    this.form.controls.Entity.valueChanges.subscribe(async (value) => {
      this.plans = [];
      this.aliquotTaxs = [];
      this.initPlanFilter();
      this.initAliquotTaxFilter();

      this.form.patchValue(
        {
          Plan: null,
          AliquotTax: null,
        },
        { emitEvent: false }
      );

      if (!value || typeof value === "string") {
        return;
      } else {
        await this.loadInsurancePlan(value);
      }
    });

    this.form.controls.Plan.valueChanges.subscribe(async (value) => {
      this.aliquotTaxs = [];
      this.form.patchValue(
        {
          AliquotTax: null,
        },
        { emitEvent: false }
      );

      this.initAliquotTaxFilter();

      if (!value || typeof value === "string") {
        return;
      }
      await this.loadAliquotTax(value);
    });

    this.form.valueChanges.subscribe((value) => {
      const plan = new IntegratedAdmissionAppointmentPlan();
      plan.AliquotTax =
        value.AliquotTax && typeof value.AliquotTax !== "string"
          ? value.AliquotTax
          : null;
      plan.Plan =
        value.Plan && typeof value.Plan !== "string" ? value.Plan : null;
      plan.Entity =
        value.Entity && typeof value.Entity !== "string" ? value.Entity : null;

      this.service.setPlan(plan);
      this.service.setName(value.Name);
    });
  }

  private async loadHealthInsurance(entityId: number = null) {
    this.entities =
      this.entities.length > 0
        ? this.entities
        : await this.entityService.getEntities(true);

    this.filterCompleteEntity = new FilterCompleteService<Entity>(
      this.entities,
      this.form.controls.Entity
    );

    if (entityId) {
      const entityIndex = this.entities.findIndex((x) => x.Id === entityId);
      this.form.patchValue(
        {
          Entity: this.entities[entityIndex],
        },
        { emitEvent: false }
      );
    }
  }

  private async loadInsurancePlan(entity: Entity, planId: number = null) {
    this.plans = await this.entityService.getPlansByEntityId(entity.Id);
    this.filterCompletePlan = new FilterCompleteService<Plan>(
      this.plans,
      this.form.controls.Plan
    );

    if (planId) {
      const planIndex = this.plans.findIndex((x) => x.Id === planId);
      this.form.patchValue(
        {
          Plan: this.plans[planIndex],
        },
        { emitEvent: false }
      );
    }
  }

  private async loadAliquotTax(plan: Plan, aliquotTaxId: number = null) {
    this.aliquotTaxs = await this.alicuotTaxService.getAliquotsTaxByPlanId(
      plan.Id
    );
    this.filterCompleteAliquotTax = new FilterCompleteService<AliquotTax>(
      this.aliquotTaxs,
      this.form.controls.AliquotTax
    );

    if (aliquotTaxId) {
      const aliquotTaxIndex = this.aliquotTaxs.findIndex(
        (x) => x.Id === aliquotTaxId
      );
      this.form.patchValue(
        {
          AliquotTax: this.aliquotTaxs[aliquotTaxIndex],
        },
        { emitEvent: false }
      );
    }
  }

  private initPlanFilter() {
    this.filterCompletePlan = new FilterCompleteService<Plan>(
      this.plans,
      this.form.controls.Plan
    );
  }

  private initAliquotTaxFilter() {
    this.filterCompleteAliquotTax = new FilterCompleteService<AliquotTax>(
      this.aliquotTaxs,
      this.form.controls.AliquotTax
    );
  }

  private initFilters() {
    this.filterCompleteEntity = new FilterCompleteService<Entity>(
      this.entities,
      this.form.controls.Entity
    );

    this.initPlanFilter();
    this.initAliquotTaxFilter();
  }

  ngOnDestroy(): void {
    if (this.currentBudgetSuscripcion) {
      this.currentBudgetSuscripcion.unsubscribe();
    }
  }
}
